import { Activity, CalendarDays } from 'lucide-react';
import { EmptyStateCard } from './EmptyStateCard';

interface HeatmapCardProps {
  heatmapFiltro: 'todos' | 'escolas' | 'usuarios';
  setHeatmapFiltro: (f: 'todos' | 'escolas' | 'usuarios') => void;
  heatmapWeeks: { date: Date; days: number[] }[];
  heatmapLoading: boolean;
  onDayClick: (date: Date, intensity: number) => void;
}

export function HeatmapCard({ heatmapFiltro, setHeatmapFiltro, heatmapWeeks, heatmapLoading, onDayClick }: HeatmapCardProps) {
  const colors = ['#f1f5f9', '#c7d2fe', '#818cf8', '#6366f1', '#4338ca'];
  const hasData = heatmapWeeks.some(w => w.days.some(d => d > 0));

  return (
    <div style={{
      background: 'var(--bg-card, white)', borderRadius: 16,
      border: '1px solid var(--border-color, #e5e7eb)', padding: 14,
      color: 'var(--text-main, #111827)', display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ fontSize: '.95rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-main, #111827)', margin: 0 }}>
          <Activity size={16} style={{ color: '#6366f1' }} />
          Acessos à plataforma
          {heatmapLoading && <span style={{ fontSize: '.75rem', color: '#94a3b8', fontWeight: 400 }}>carregando...</span>}
        </h3>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['todos', 'escolas', 'usuarios'] as const).map(f => (
            <button key={f} onClick={() => setHeatmapFiltro(f)} style={{
              padding: '4px 10px', borderRadius: 6, border: '1px solid',
              fontSize: '.75rem', fontWeight: 600, cursor: 'pointer',
              borderColor: heatmapFiltro === f ? '#6366f1' : '#e2e8f0',
              background: heatmapFiltro === f ? '#6366f1' : 'transparent',
              color: heatmapFiltro === f ? 'white' : '#64748b',
              transition: 'all .2s',
            }}>
              {f === 'todos' ? 'Todos' : f === 'escolas' ? 'Escolas' : 'Usuários'}
            </button>
          ))}
        </div>
      </div>

      {!hasData && !heatmapLoading ? (
        <EmptyStateCard
          icon={CalendarDays}
          title="Sem dados de acesso ainda"
          description="Os acessos serão registrados assim que usuários começarem a utilizar a plataforma."
          compact
        />
      ) : (
        <>
          {/* Grid */}
          <div style={{ position: 'relative', overflowX: 'auto', paddingBottom: 2, scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}>
            <div style={{ display: 'flex', width: 'max-content' }}>
              {/* Dias da semana */}
              <div style={{ display: 'flex', flexDirection: 'column', paddingRight: 8, width: 28, flexShrink: 0 }}>
                <div style={{ height: 16, marginBottom: 4 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '.7rem', color: 'var(--text-muted, #6b7280)' }}>
                  {['', 'Seg', '', 'Qua', '', 'Sex', ''].map((label, i) => (
                    <div key={i} style={{ height: 12, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', lineHeight: 1 }}>{label}</div>
                  ))}
                </div>
              </div>
              {/* Meses + células */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', position: 'relative', height: 16, fontSize: '.7rem', color: 'var(--text-main, #374151)' }}>
                  {heatmapWeeks.map((week, index) => {
                    const isFirst = index === 0 || week.date.getMonth() !== heatmapWeeks[index - 1].date.getMonth();
                    if (!isFirst) return null;
                    const monthName = week.date.toLocaleString('pt-BR', { month: 'short' }).replace('.', '');
                    return <span key={index} style={{ position: 'absolute', left: index * 16, textTransform: 'capitalize' }}>{monthName}</span>;
                  })}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {heatmapWeeks.map((week, weekIndex) => (
                    <div key={weekIndex} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {week.days.map((intensity, dayIndex) => {
                        const dayDate = new Date(week.date);
                        dayDate.setDate(dayDate.getDate() + dayIndex);
                        return (
                          <div
                            key={dayIndex}
                            onClick={() => onDayClick(dayDate, intensity)}
                            style={{
                              width: 12, height: 12, flexShrink: 0,
                              backgroundColor: colors[intensity],
                              borderRadius: 3,
                              outline: intensity === 0 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                              outlineOffset: -1,
                              cursor: intensity > 0 ? 'pointer' : 'default',
                              transition: 'transform .1s',
                            }}
                            title={`${dayDate.toLocaleDateString('pt-BR')}\n${intensity === 0 ? 'Nenhum acesso' : intensity < 2 ? 'Poucos acessos' : intensity < 3 ? 'Médio volume' : intensity < 4 ? 'Alto volume' : 'Muito alto volume'}`}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.75rem', color: 'var(--text-muted, #6b7280)' }}>
            <span>Clique em um dia para ver o detalhamento de auditoria</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ marginRight: 4 }}>Menos</span>
              {colors.map(c => (
                <div key={c} style={{ width: 12, height: 12, backgroundColor: c, borderRadius: 3, outline: c === '#f1f5f9' ? '1px solid rgba(0,0,0,0.05)' : 'none', outlineOffset: -1 }} />
              ))}
              <span style={{ marginLeft: 4 }}>Mais</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
